import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import { BoardAction, BoardActionType } from './board-actions.model';
import { BOARD_ANNOTATION_OPTIONS } from '@/values/ed-whiteboard.values';

export default class EdWboDrawerManageState {

    private wboDrawerTools: WboDrawerTools;
    private currentBoard: string;
    private children: Map<string, any[]>;
    private HAND_UPDATE_INTERVAL: number = 2000;

    constructor(wboDrawerTools: WboDrawerTools) {
        this.wboDrawerTools = wboDrawerTools;
        this.currentBoard = this.wboDrawerTools.getCurrentBoard();
        this.children = new Map<string, any[]>();
    }

    public manageAnnotation(data: any, isMadeByUser: boolean): void {

        let type: string = data.type;
        let annotationId: string = data.id;

        switch (type) {

            case 'delete':
                if (annotationId && (this.wboDrawerTools.isHostUser || this.wboDrawerTools.username === data.userName)) {
                    this.deleteAnnotations([annotationId], isMadeByUser);
                }
                break;

            case 'update':
                if (annotationId && (this.wboDrawerTools.isHostUser || this.wboDrawerTools.username === data.userName)) {
                    this.updateAnnotation(data, isMadeByUser);
                }
                break;

            case 'child':
                break;

            default: //Add data
                this.addAnnotation(data, isMadeByUser);
                break;
        }
    }

    private isValidNumber(number: any): boolean {
        return !Number.isNaN(number);
    }

    public getBoardActionObjectByActionType(annotation: any, boardActionType: BoardActionType): any {
        let boardActionObject: any = {};

        switch (boardActionType) {

            case BoardActionType.DELETE:
                boardActionObject = {
                    id: annotation.id,
                    type: BoardActionType.DELETE
                }
                break;

            case BoardActionType.UPDATE:
                boardActionObject = {
                    id: annotation.id,
                    type: BoardActionType.UPDATE,
                    userName: annotation.userName,
                    tool: annotation.tool,
                    deltax: annotation.deltax ? annotation.deltax : 0,
                    deltay: annotation.deltay ? annotation.deltay : 0
                };
                break;

            case BoardActionType.ADD: {
                boardActionObject = {
                    id: annotation.id,
                    type: annotation.type,
                    color: annotation.color,
                    size: annotation.size,
                    opacity: annotation.opacity,
                    userName: annotation.userName,
                    tool: annotation.tool,
                    _children: annotation._children
                };
                if (this.isValidNumber(annotation.x)) {
                    boardActionObject.x = annotation.x;
                }
                if (this.isValidNumber(annotation.y)) {
                    boardActionObject.y = annotation.y;
                }
                if (this.isValidNumber(annotation.x2)) {
                    boardActionObject.x2 = annotation.x2;
                }
                if (this.isValidNumber(annotation.y2)) {
                    boardActionObject.y2 = annotation.y2;
                }
                if (this.isValidNumber(annotation.deltax)) {
                    boardActionObject.deltax = annotation.deltax;
                }
                if (this.isValidNumber(annotation.deltay)) {
                    boardActionObject.deltay = annotation.deltay;
                }
                break;
            }

            default:
                break;
        }

        return boardActionObject;
    }

    private getBoardActionObjectList(annotations: any[], actionType: BoardActionType, takeDataFromPersistentBoard: boolean): any[] {
        let boardActionObjectList: any[] = [];

        for (const annotation of annotations) {
            if (annotation._children && Array.isArray(annotation._children) && annotation._children.length > 0) {
                boardActionObjectList.push(annotation);
                continue;
            }
            const storedAnnotation = this.wboDrawerTools.getPersistentBoardData()?.getAnnotationDataById(annotation.id);
            const annotationData = (takeDataFromPersistentBoard && storedAnnotation) ? storedAnnotation : annotation;
            const boardActionObject = this.getBoardActionObjectByActionType(annotationData, actionType);
            boardActionObjectList.push(boardActionObject);
        }
        return boardActionObjectList;
    }

    private cleanRedoBoardActionStack(): void {
        this.wboDrawerTools.getUndoRedoStackFunctions().clean(BOARD_ANNOTATION_OPTIONS.redo);
    }

    private getUndoStackLastRecord(): any {
        return this.wboDrawerTools.getUndoRedoStackFunctions().peek(BOARD_ANNOTATION_OPTIONS.undo);
    }

    private getRedoStackLastRecord(): any {
        return this.wboDrawerTools.getUndoRedoStackFunctions().peek(BOARD_ANNOTATION_OPTIONS.redo);
    }

    private addBoardActionToStack(boardAnnotationOption: string, annotations: any[], actionType: BoardActionType, actionParams: any, takeDataFromPersistentBoard: boolean) {
        let boardActionObjectList: any[] = this.getBoardActionObjectList(annotations, actionType, takeDataFromPersistentBoard);
        if (boardActionObjectList && boardActionObjectList.length > 0) {
            let boardAction = new BoardAction(boardActionObjectList, actionType, actionParams);
            let lastStackRecord = BOARD_ANNOTATION_OPTIONS.undo === boardAnnotationOption ? this.getUndoStackLastRecord() : this.getRedoStackLastRecord();
            if (lastStackRecord && lastStackRecord[this.currentBoard] && Object.keys(lastStackRecord[this.currentBoard]).length === 0) {
                lastStackRecord[this.currentBoard] = boardAction;
            } else {
                const annotatedBoards = {};
                annotatedBoards[this.currentBoard] = boardAction;
                this.wboDrawerTools.getUndoRedoStackFunctions().push(boardAnnotationOption, annotatedBoards);
            }
        }
    }

    public addBoardActionToUndoStack(annotations: any[], actionType: BoardActionType, actionParams: any, takeDataFromPersistentBoard: boolean): void {
        this.addBoardActionToStack(BOARD_ANNOTATION_OPTIONS.undo, annotations, actionType, actionParams, takeDataFromPersistentBoard);
    }

    public addBoardActionToRedoStack(annotations: any[], actionType: BoardActionType, actionParams: any = null, takeDataFromPersistentBoard: boolean): void {
        this.addBoardActionToStack(BOARD_ANNOTATION_OPTIONS.redo, annotations, actionType, actionParams, takeDataFromPersistentBoard);
    }

    private addAnnotation(annotation: any, isMadeByUser: boolean): void {
        if (isMadeByUser) {
            this.addBoardActionToUndoStack([annotation], BoardActionType.DELETE, null, false);
            this.cleanRedoBoardActionStack();
        }
    }

    private updateAnnotation(annotation: any, isMadeByUser: boolean): void {

        const isPositionUpdate: boolean = isMadeByUser && (annotation.deltax || annotation.deltay);
        if (isPositionUpdate) {

            const now: number = performance.now();
            const lastUndoRecord: any = this.getUndoStackLastRecord();
            const lastUndoBoardAction: BoardAction = lastUndoRecord ? lastUndoRecord[this.currentBoard] : null;
            const isLastUndoBoardActionUpdate = BoardAction.isValidBoardAction(lastUndoBoardAction) && (BoardActionType.UPDATE === lastUndoBoardAction.actionType) && (annotation.id == lastUndoBoardAction.annotations[0].id);

            if (!isLastUndoBoardActionUpdate) {
                this.addBoardActionToUndoStack([annotation], BoardActionType.UPDATE, { date: now }, true);
                this.cleanRedoBoardActionStack();
                return;
            }

            const lastUpdateDate: number = lastUndoBoardAction.actionParams.date;

            if ((now - lastUpdateDate) > this.HAND_UPDATE_INTERVAL) {
                this.addBoardActionToUndoStack([annotation], BoardActionType.UPDATE, { date: now }, false);
                this.cleanRedoBoardActionStack();

            } else {
                lastUndoBoardAction.actionParams.date = now;
            }
        }
    }

    private getAnnotationDataFromPersistentBoardData(annotationIds: string[]): any[] {
        let annotations: any[] = [];
        annotationIds.forEach(annotationId => {
            const annotation = this.wboDrawerTools.getPersistentBoardData()?.getAnnotationDataById(annotationId);
            if (annotation) {
                annotations.push(annotation);
            }
        });
        return annotations;
    }

    private saveAnnotationChildren(annotations: any[]): void {
        annotations.forEach(annotation => {
            if (annotation.id && annotation._children && !this.children.get(annotation.id)) {
                this.children.set(annotation.id, annotation._children);
            }
        });
    }

    public getAnnotationChildren(): Map<string, any[]> {
        return this.children;
    }

    public deleteAnnotations(annotationIds: string[], isMadeByUser: boolean): void {
        const annotations: any[] = this.getAnnotationDataFromPersistentBoardData(annotationIds);
        if (annotations && annotations.length > 0) {
            this.saveAnnotationChildren(annotations);

            if (isMadeByUser) {
                if (annotations.length > 1){ // When deleting all annotations, clear undo/redo stacks
                    this.cleanUndoRedoStacks();
                } else {
                    this.addBoardActionToUndoStack(annotations, BoardActionType.ADD, null, true);
                    this.cleanRedoBoardActionStack();
                }
            }
        }
    }

    private deleteCurrentBoardFromLastBoardAction(boardAnnotationOption: string, boardAction: any): void {

        delete boardAction[this.currentBoard];

        if (Object.keys(boardAction).length === 0) {
            this.wboDrawerTools.getUndoRedoStackFunctions().pop(boardAnnotationOption);
        }
    }

    private getLastBoardAction(boardAnnotationOption: string): BoardAction {

        let boardAction: BoardAction = null;
        const stackLastRecord: any = BOARD_ANNOTATION_OPTIONS.undo === boardAnnotationOption ? this.getUndoStackLastRecord() : this.getRedoStackLastRecord();

        if (stackLastRecord && stackLastRecord[this.currentBoard]) {
            boardAction = stackLastRecord[this.currentBoard];
            this.deleteCurrentBoardFromLastBoardAction(boardAnnotationOption, stackLastRecord);
        }

        return boardAction;
    }

    public getLastUndoBoardAction(): BoardAction {
        return this.getLastBoardAction(BOARD_ANNOTATION_OPTIONS.undo);
    }

    public getLastRedoBoardAction(): BoardAction {
        return this.getLastBoardAction(BOARD_ANNOTATION_OPTIONS.redo);
    }

    /**
     * Clears both undo and redo stacks if any of the given annotation IDs exist in either stack.
     * @param annotationIds Array of annotation IDs to check for in the stacks.
     */
    public clearUndoRedoStacksIfAnnotationIdsExist(annotationIds: string[]): void {

        const undoStack = this.wboDrawerTools.getUndoRedoStackFunctions().getAll(BOARD_ANNOTATION_OPTIONS.undo);
        const redoStack = this.wboDrawerTools.getUndoRedoStackFunctions().getAll(BOARD_ANNOTATION_OPTIONS.redo);

        // Helper to check if any annotationId exists in a stack object
        const stackHasAnyId = (stack: any): boolean => {
            if (!stack) return false;
            // stack is an object with board ids as keys, each value is a BoardAction
            return Object.values(stack).some((boardAction: any) => {
                if (!boardAction || !Array.isArray(boardAction[this.currentBoard]?.annotations)) return false;
                return boardAction[this.currentBoard].annotations.some((ann: any) => annotationIds.includes(ann.id));
            });
        };

        if (stackHasAnyId(undoStack) || stackHasAnyId(redoStack)) {
            this.cleanUndoRedoStacks();
        }
    }

    public cleanUndoRedoStacks(): void {
        this.wboDrawerTools.getUndoRedoStackFunctions().clean(BOARD_ANNOTATION_OPTIONS.undo);
        this.wboDrawerTools.getUndoRedoStackFunctions().clean(BOARD_ANNOTATION_OPTIONS.redo);
    }
}
